import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import { type ICompanyApplicantData } from './CompanyApplicantData.ts';

export interface ISubjectDataType {
  code: string;
  label: string;
  description?: string;
  type: Datatype;
}

export function createSubjectDataType(
  code: string,
  config: Omit<ISubjectDataType, 'code'>,
): ISubjectDataType {
  return {
    code,
    ...config,
  };
}

const SubjectDataTypesAll = {
  name: createSubjectDataType('name', {
    label: 'Nome',
    type: Datatype.String,
  }),
  familyName: createSubjectDataType('familyName', {
    label: 'Cognome',
    type: Datatype.String,
  }),
  genre: createSubjectDataType('genre', {
    label: 'Genere',
    type: Datatype.String,
  }),
  fiscalNumber: createSubjectDataType('fiscalNumber', {
    label: 'Codice Fiscale',
    type: Datatype.String,
  }),
  vatNumber: createSubjectDataType('vatNumber', {
    label: 'Partita IVA',
    type: Datatype.String,
  }),
  // fiscalNumberSimple: createApplicantDataType('fiscalNumberSimple', {
  //   label: 'Codice Fiscale italiano',
  //   description: 'Codice fiscale senza prefisso TINIT-',
  //   type: Datatype.String,
  // }),
  placeOfBirth: createSubjectDataType('placeOfBirth', {
    label: 'Codice Belfiore luogo di nascita',
    description: 'Codice Belfiore del comune di nascita',
    type: Datatype.String,
  }),
  placeOfBirthName: createSubjectDataType('placeOfBirthName', {
    label: 'Comune di nascita',
    type: Datatype.String,
  }),
  countyOfBirth: createSubjectDataType('countyOfBirth', {
    label: 'Codice provincia di nascita',
    description: "Codice della provincia di nascita, ad esempio 'MI'",
    type: Datatype.String,
  }),
  dateOfBirth: createSubjectDataType('dateOfBirth', {
    label: 'Data di nascita',
    type: Datatype.Date,
  }),
  digitalAddress: createSubjectDataType('digitalAddress', {
    label: 'Indirizzo PEC',
    type: Datatype.String,
  }),
  email: createSubjectDataType('email', {
    label: 'Indirizzo email',
    type: Datatype.String,
  }),
  idCard: createSubjectDataType('idCard', {
    label: 'ID documento di identità',
    type: Datatype.String,
  }),
  idCardType: createSubjectDataType('idCardType', {
    label: 'Tipo di documento di identità',
    type: Datatype.String,
  }),
  idCardIssuer: createSubjectDataType('idCardIssuer', {
    label: 'Comune di rilascio documento di identità',
    type: Datatype.String,
  }),
  idCardIssueDate: createSubjectDataType('idCardIssueDate', {
    label: 'Data di rilascio documento di identità',
    type: Datatype.Date,
  }),
  idCardExpirationDate: createSubjectDataType('idCardExpirationDate', {
    label: 'Data di scadenza documento di identità',
    type: Datatype.Date,
  }),
  mobilePhone: createSubjectDataType('mobilePhone', {
    label: 'Numero di telefono',
    type: Datatype.String,
  }),
  domicileStreetAddress: createSubjectDataType('domicileStreetAddress', {
    label: 'Indirizzo di domicilio',
    type: Datatype.String,
  }),
  domicilePostalCode: createSubjectDataType('domicilePostalCode', {
    label: 'CAP di domicilio',
    type: Datatype.String,
  }),
  domicileMunicipality: createSubjectDataType('domicileMunicipality', {
    label: 'Comune di domicilio',
    type: Datatype.String,
  }),
  domicileProvince: createSubjectDataType('domicileProvince', {
    label: 'Provincia di domicilio',
    type: Datatype.String,
  }),
  domicileNation: createSubjectDataType('domicileNation', {
    label: 'Nazione di domicilio',
    type: Datatype.String,
  }),
  // - Indirizzo di residenza del titolare
  residenceAddress: createSubjectDataType('residenceAddress', {
    label: 'Indirizzo di residenza',
    type: Datatype.String,
  }),
  // - CAP di residenza del titolare
  residencePostalCode: createSubjectDataType('residencePostalCode', {
    label: 'CAP di residenza',
    type: Datatype.String,
  }),
  // - Comune di residenza del titolare
  residenceMunicipality: createSubjectDataType('residenceMunicipality', {
    label: 'Comune di residenza',
    type: Datatype.String,
  }),
  // - Provincia di residenza del titolare
  residenceProvince: createSubjectDataType('residenceProvince', {
    label: 'Provincia di residenza',
    type: Datatype.String,
  }),
  // - Nazione di residenza del titolare
  residenceNation: createSubjectDataType('residenceNation', {
    label: 'Nazione di residenza',
    type: Datatype.String,
  }),
  // - Numero civico di residenza (nel caso lo si voglia tenere separato dalla via)
  residenceCivicNumber: createSubjectDataType('residenceCivicNumber', {
    label: 'Numero civico di residenza',
    type: Datatype.String,
  }),
  // campo "dataScadenza" in datiAnagraficiAggiuntivi
  //(?)
  // expirationDate: createApplicantDataType('expirationDate', {
  //   label: 'Data di scadenza',
  //   description: '',
  //   type: Datatype.Date,
  // }),
  // - Sede legale
  legalHeadquarters: createSubjectDataType('legalHeadquarters', {
    label: 'Sede legale',
    type: Datatype.String,
  }),
};

type SubjectDataTypesSchema = typeof SubjectDataTypesAll;
export type SubjectDataKey = keyof SubjectDataTypesSchema;

export type ISubjectData = {
  [K in keyof SubjectDataTypesSchema]: string | undefined;
} & {
  company: ICompanyApplicantData | undefined;
};

export class SubjectDataTypes {
  static Map = SubjectDataTypesAll;

  static List = Object.values(SubjectDataTypesAll);

  static Codes = Object.keys(SubjectDataTypesAll) as Array<
    keyof typeof SubjectDataTypesAll
  >;

  /**
   * Ritorna true se la chiave passata è una chiave di dati del titolare.
   * (e.g. name, familyName, ...)
   */
  static isSubjectDataCode(
    key: string | null | undefined,
  ): key is SubjectDataKey {
    if (!key) return false;
    return SubjectDataTypes.Codes.includes(key as SubjectDataKey);
  }
}
